import CustomButton from '@/components/atoms/CustomButton';
import Loader from '@/components/atoms/Loader';
import UserProfileOverview from '@/components/organisms/UserProfileOverview';
import AppPageTemplate from '@/components/templates/AppPageTemplate';
import { mixpanelEvents } from '@/constants/mixpanel';
import useToast from '@/hooks/useToast';
import useTelemetry from '@/providers/TelemetryProvider/useTelemetry';
import { uploadProfilePhoto } from '@/services/api/requests/user';
import { useAppStore } from '@/store/useAppStore';
import { useNavigate } from '@tanstack/react-router';
import React, { useCallback, useLayoutEffect, useState } from 'react';
import roles from '@/constants/roles';
import { getUnacceptedContentPolicy } from '@/utils/onboardingUtils';

interface Props {
  setOnboardingStep: (step: number) => void;
  isSkippingDemographics?: boolean;
}

const ProfileOverview = ({ setOnboardingStep, isSkippingDemographics = false }: Props) => {
  const toast = useToast();
  const navigate = useNavigate();
  const { trackEvent } = useTelemetry();

  const [isLoading, setIsLoading] = useState(false);

  useLayoutEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const { user: userData, legalAcceptanceStatuses, setUser } = useAppStore();

  const uploadPhoto = async (profilePhoto: File) => {
    try {
      const data = await uploadProfilePhoto(profilePhoto);
      setUser(data);
      toast({
        status: 'success',
        title: 'Success',
        description: 'Profile Photo updated successfully!',
        position: 'top-right',
      });
      return true;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      return false;
    }
  };

  const onClickProceed = useCallback(() => {
    setIsLoading(true);
    trackEvent(mixpanelEvents.USER_SIGN_UP_DONE, userData?.user_metadata);
    const isContentCreatorOnly =
      !!userData && userData.roles.includes(roles.contentCreator) && userData.roles.length === 1;
    const hasPendingContentPolicy = Boolean(getUnacceptedContentPolicy(legalAcceptanceStatuses));
    const nextRoute =
      isContentCreatorOnly && hasPendingContentPolicy
        ? '/creator/apply/content-policy'
        : isContentCreatorOnly
          ? '/creator/profile'
          : '/onboarding/wholeness-screener';

    navigate({ to: nextRoute }).catch(() => setIsLoading(false));
  }, [legalAcceptanceStatuses, navigate, trackEvent, userData]);

  if (!userData) return <Loader />;

  return (
    <AppPageTemplate>
      <UserProfileOverview
        showSkippedDemographics={(isSkippingDemographics || !!userData.user_metadata.demographicsSkippedAt) && !userData.user_metadata.dateOfBirth}
        title={'Create Your Profile'}
        updateProfilePhoto={uploadPhoto}
        userData={{ ...userData.user_metadata, email: userData.email }}
        onClickEditContactDetails={() => setOnboardingStep(0)}
        onClickEditDemographics={() => setOnboardingStep(1)}
        initialPreview={userData?.user_metadata.profilePicture}
      />
      <CustomButton
        label="Proceed"
        backgroundColor="secondary.500"
        width={'295px'}
        labelColor="white"
        style={{ marginBottom: '30px' }}
        onClick={onClickProceed}
        isLoading={isLoading}
        disabled={isSkippingDemographics}
      />
    </AppPageTemplate>
  );
};

export default ProfileOverview;
